import React, { useState, useEffect } from 'react';
import { clickable } from '../../../utils/a11y';
import { useNavigate } from 'react-router-dom';
import { FaSearch, FaBuilding, FaArrowRight, FaBars, FaBell, FaUserCircle, FaSignOutAlt, FaHistory, FaServer, FaLayerGroup } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { getApiEndpoint } from '../../../config/api';
import { clearToken } from '../../../utils/auth';
import CloudStaffSidebar from './CloudStaffSidebar';
import cloudsafeLogo from '../../../assets/cloudsafe-logo.png';
import './AssetGenerationV2Page.css';

export default function AssetGenerationV2Page() {
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [userName, setUserName] = useState('');

  useEffect(() => {
    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
    try {
      const [profileRes, propsRes] = await Promise.all([
        fetch(getApiEndpoint('/api/users/profile'), { headers }),
        fetch(getApiEndpoint('/api/properties'), { headers }),
      ]);
      if (profileRes.ok) {
        const d = await profileRes.json();
        setUserName(`${d.firstName || d.first_name || ''} ${d.lastName || d.last_name || ''}`.trim());
      }
      if (propsRes.ok) {
        const data = await propsRes.json();
        const list = Array.isArray(data) ? data : (data.properties || []);
        setProperties(list);
        setFiltered(list);
      } else {
        toast.error('Could not load properties. Try signing out and back in.');
      }
    } catch {
      toast.error('Failed to load properties');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const q = query.toLowerCase().trim();
    if (!q) { setFiltered(properties); return; }
    setFiltered(properties.filter(p =>
      (p.name || '').toLowerCase().includes(q) ||
      (p.address || '').toLowerCase().includes(q)
    ));
  }, [query, properties]);

  const handleSelect = (propertyId) => {
    navigate(`/cloud-staff/asset-generation-v2/${propertyId}`);
  };

  const handleLogout = () => {
    clearToken();
    navigate('/login');
    toast.success('Logged out successfully');
  };

  return (
    <div className="cs-dashboard">
      <header className="cs-header">
        <div className="cs-header-left">
          <button className="cs-mobile-menu-btn" onClick={() => setMobileSidebarOpen(true)} aria-label="Open menu">
            <FaBars />
          </button>
          <img src={cloudsafeLogo} alt="CloudSafe" className="cs-logo" />
          <div className="cs-header-badge"><FaServer /><span>Cloud Staff Portal</span></div>
        </div>
        <div className="cs-header-right">
          <div className="cs-header-icons">
            <button className="cs-icon-btn" title="Notifications"><FaBell /></button>
            <div className="cs-user-avatar" aria-haspopup="menu" aria-expanded={showUserMenu}
                 {...clickable(() => setShowUserMenu(!showUserMenu), { label: 'User menu' })}>
              <FaUserCircle />
              {showUserMenu && (
                <div className="cs-user-menu">
                  <div className="cs-user-menu-header">
                    <FaUserCircle className="cs-menu-avatar" />
                    <div>
                      <p className="cs-menu-name">{userName || 'Cloud Staff'}</p>
                      <p className="cs-menu-role">Cloud Staff Admin</p>
                    </div>
                  </div>
                  <div className="cs-user-menu-divider" />
                  <button onClick={() => navigate('/cloud-staff/activity-log')} className="cs-menu-item"><FaHistory /> Activity Log</button>
                  <button onClick={handleLogout} className="cs-menu-item cs-menu-logout"><FaSignOutAlt /> Sign Out</button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      <div className="cs-layout">
        <CloudStaffSidebar
          active="asset-generation-v2"
          mobileOpen={mobileSidebarOpen}
          onClose={() => setMobileSidebarOpen(false)}
        />

        <main className="cs-content">
          <div className="agv2-header">
            <div className="agv2-header-icon"><FaLayerGroup /></div>
            <div>
              <h1 className="agv2-title">Asset Generation v2</h1>
              <p className="agv2-subtitle">Select a property to begin a new onboarding session — upload a floor plan, drop pins, categorise, and generate asset IDs. The building’s ID prefix is applied automatically from that property (or derived from its name).</p>
            </div>
          </div>

          <div className="agv2-search-bar">
            <FaSearch className="agv2-search-icon" />
            <input
              type="text"
              className="agv2-search-input"
              placeholder="Search properties by name or address…"
              value={query}
              onChange={e => setQuery(e.target.value)}
              autoFocus
            />
          </div>

          {loading ? (
            <div className="agv2-loading">
              <div className="cs-spinner" />
              <p>Loading properties…</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="agv2-empty">
              <FaBuilding className="agv2-empty-icon" />
              <p>{query ? 'No properties match your search.' : 'No properties found.'}</p>
            </div>
          ) : (
            <div className="agv2-grid">
              {filtered.map(property => (
                <button
                  key={property.id}
                  className="agv2-card"
                  onClick={() => handleSelect(property.id)}
                >
                  <div className="agv2-card-icon">
                    <FaBuilding />
                  </div>
                  <div className="agv2-card-body">
                    <span className="agv2-card-name">{property.name || `Property #${property.id}`}</span>
                    {property.property_code && (
                      <span className="agv2-card-code" title="Property ID prefix for generated asset tags">
                        {property.property_code}
                      </span>
                    )}
                    {property.address && (
                      <span className="agv2-card-address">{property.address}</span>
                    )}
                  </div>
                  <FaArrowRight className="agv2-card-arrow" />
                </button>
              ))}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
